define((require) => {
    "use strict";

    const $                    = require("jquery");
    const _                    = require("underscore");
    const Browser              = require("shared/services/Browser");
    const Class                = require("shared/util/Class");
    const ExperienceViewModel  = require("shared/ui/viewer/ExperienceViewModel");
    const Preconditions        = require("shared/util/Preconditions");
    const Promise              = require("shared/util/Promise");
    const SceneTransition      = require("shared/ui/experience/SceneTransition").default;
    const SceneTransitionTypes = require("shared/ui/experience/SceneTransitionTypes").default;
    const TweenMax             = require("TweenMax");

    const DEFAULT_TRANSITION_DURATION = 0.5; // 500 milliseconds

    /**
     * Handles the various transitions between scenes.
     *
     * @author Kim
     * @class SceneTransitionController
     * @constructor
     *
     * @param {ExperienceViewModel} experienceViewModel The view model for the experience.
     */
    return Class.extend({

        _experienceViewModel: null,

        _currentSceneViewer: null,

        _previousViewer: null,

        _deferreds: null,

        constructor(experienceViewModel) {
            this._experienceViewModel = experienceViewModel;
            this._deferreds = [];
        },

        _scaledExperienceSize() {
            return this._experienceViewModel.get("experience").getSize(true);
        },

        _playTransition(sceneViewer, transition, duration) {
            const options = {
                force3D: Browser.capabilities.is3dAnimationsSupported
            };
            const size = this._scaledExperienceSize();

            let from = true;
            let out = false;

            // For simplicity, all transitions start at 0,0
            TweenMax.set(sceneViewer.el, {x: 0, y: 0});

            switch (transition) {
                // Horizontal
                case SceneTransition.MOVE_FROM_RIGHT:
                    options.x = size.width;
                    break;

                case SceneTransition.MOVE_FROM_LEFT:
                    options.x = -size.width;
                    break;

                case SceneTransition.MOVE_TO_LEFT:
                    from = false;
                    out = true;
                    options.x = -size.width;
                    break;

                case SceneTransition.MOVE_TO_RIGHT:
                    from = false;
                    out = true;
                    options.x = size.width;
                    break;

                // Fade
                case SceneTransition.FADE_IN:
                    TweenMax.set(sceneViewer.el, {opacity: 0});
                    from = false;
                    options.opacity = 1;
                    break;

                case SceneTransition.FADE_OUT:
                    from = false;
                    out = true;
                    options.opacity = 0;
                    break;

                // Vertical
                case SceneTransition.MOVE_FROM_TOP:
                    options.y = -size.height;
                    break;

                case SceneTransition.MOVE_TO_BOTTOM:
                    from = false;
                    out = true;
                    options.y = size.height;
                    break;

                case SceneTransition.MOVE_FROM_BOTTOM:
                    options.y = size.height;
                    break;

                case SceneTransition.MOVE_TO_TOP:
                    from = false;
                    out = true;
                    options.y = -size.height;
                    break;

                default:
                    // TODO Candidate for refactoring during MOS-2934.
                    Preconditions.warn(`DefaultSwitchWarn - The transition '${transition}' is not recognized.`);
                    break;
            }

            return $.Deferred((deferred) => {
                options.onComplete = () => {
                    deferred.resolve();

                    // Hide scenes after they are transitioned out
                    if (out) {
                        sceneViewer.hide();
                    }
                };

                options.onStart = () => {
                    // Let's make sure the target element is always shown
                    sceneViewer.show();
                };

                const tweenFunction = from ? TweenMax.from : TweenMax.to;
                const tweenDuration = _.isNumber(duration) ? duration : DEFAULT_TRANSITION_DURATION;

                if (tweenDuration === 0) {
                    // Manually invoke onStart if the duration is 0, i.e. a 'NONE' scene transition.
                    // Note that onComplete will be trigged...
                    options.onStart();
                    // No need to pass it on...
                    delete options.onStart;
                }

                tweenFunction.call(TweenMax, sceneViewer.el, tweenDuration, options);
            }).promise();
        },

        _removeDeferred(deferred) {
            const descriptor = _.findWhere(this._deferreds, {deferred});
            const i = this._deferreds.indexOf(descriptor);

            if (i !== -1) {
                this._deferreds.splice(i, 1);
            }
        },

        _isAnimating() {
            return this._deferreds.length > 0;
        },

        /**
         * Registers the initial scenes, making sure to show the current
         * scene and apply any (initial) CSS properties.
         *
         * This method should only be called once/initially.
         *
         * @param {SceneViewer} currentSceneViewer The initial scene viewer.
         * @param {SceneViewer} overlaySceneViewer The overlay scene viewer.
         * @param {SceneViewer} backgroundSceneViewer The background scene viewer.
         */
        registerInitialSceneViewers(currentSceneViewer, overlaySceneViewer, backgroundSceneViewer) {
            this._currentSceneViewer = currentSceneViewer;

            // Apply default properties, making sure that the initial scenes
            // gets the proper CSS properties applied. E.g. Desktop Safari needs
            // translate3d to be applied in order to render consistently (see MOS-1532)
            TweenMax.set([currentSceneViewer.el, overlaySceneViewer.el, backgroundSceneViewer.el], {
                x: 0,
                y: 0,
                force3D: Browser.capabilities.is3dAnimationsSupported
            });

            // Show the current scene
            currentSceneViewer.show();
        },

        /**
         * Performs a transition that animates the specified <tt>sceneViewer</tt> "in"
         * and animates any previous specified scene viewer "out".
         *
         * @param  {SceneViewer} sceneViewer The scene viewer to animate in.
         *
         * @return {Deferred} A Deferred instance that will be resolved when the
         * transition completes or a new one begins.
         */
        transitionTo(sceneViewer) {
            if (sceneViewer === this._currentSceneViewer) {
                return Promise.resolved(this._currentSceneViewer, null);
            }

            const transition = this.getTransition();

            // Handle any ongoing transitions
            if (this._isAnimating()) {
                TweenMax.killTweensOf(this._currentSceneViewer.el);
                TweenMax.killTweensOf(this._previousViewer.el);

                // Resolve any pending promises
                _.invoke(this._deferreds, "resolve");
            }

            // Animate the current view "out"
            if (this._currentSceneViewer) {
                this._previousViewer = this._currentSceneViewer;
                this._playTransition(this._previousViewer, transition.out, transition.duration);
            }

            // The passed sceneViewer is now considered the current view
            this._currentSceneViewer = sceneViewer;

            const deferred = $.Deferred((deferred) => {
                this._playTransition(sceneViewer, transition.in, transition.duration).done(() => {
                    this._removeDeferred(deferred);
                    deferred.resolve(this._currentSceneViewer, this._previousViewer);
                });
            });

            // Create and store a descriptor object
            const deferredDescriptor = {
                deferred,
                resolve: () => {
                    deferred.resolve(this._currentSceneViewer, this._previousViewer);
                }
            };

            this._deferreds.push(deferredDescriptor);

            return deferred;
        },

        getTransitionType() {
            const experience = this._experienceViewModel.get("experience");
            const transitionKey = Object.keys(SceneTransitionTypes).filter((key) => SceneTransitionTypes[key].value.id === experience.get("transition"))[0];
            return transitionKey === undefined ? SceneTransitionTypes.NONE.value : SceneTransitionTypes[transitionKey].value;
        },

        getTransition() {
            const transitionType = this.getTransitionType();
            return this._experienceViewModel.get("direction") === ExperienceViewModel.DIRECTION_FORWARD ?
                transitionType.forwardTransition : transitionType.reverseTransition;
        }

    });
});



// WEBPACK FOOTER //
// ./src/shared/ui/viewer/SceneTransitionController.js